/**
 * Display-only timezone conversion.
 * Fetch ranges, booking payloads, and cutoffs stay in the region zone
 * (moment.tz.setDefault). This only shifts a wall-clock value for rendering.
 */
const STORAGE_KEY = 'bookee_display_tz'

function resolveDisplayMode(storageValue, reduxValue) {
  if (storageValue === 'local' || storageValue === 'studio') return storageValue
  if (reduxValue === 'local' || reduxValue === 'studio') return reduxValue
  return 'studio'
}

function isLocalMode(mode, allow) {
  return allow === true && mode === 'local'
}

function toDisplayMoment(input, { mode, allow, regionTz, localTz, moment }) {
  if (typeof input === 'number') {
    const instant = moment.unix(input)
    if (!isLocalMode(mode, allow) || !regionTz || !localTz) return instant
    return instant.tz(localTz)
  }
  if (!isLocalMode(mode, allow) || !regionTz || !localTz) return moment(input)

  let zoned
  if (typeof input === 'number') {
    zoned = moment.unix(input)
  } else if (moment.isMoment(input)) {
    zoned = input.clone()
  } else {
    const raw = String(input)
    // A real offset is an absolute instant. A trailing Z on wall-clock
    // strings is not, so it is removed and read in the region zone.
    if (/(?:[+-]\d{2}:?\d{2})$/.test(raw)) {
      zoned = moment.parseZone(raw)
    } else {
      const wall = raw.replace(/Z$/, '')
      zoned = moment.tz(wall, regionTz)
    }
  }
  return zoned.tz(localTz)
}

function offsetsMatch(regionTz, localTz, moment) {
  if (!regionTz || !localTz) return true
  const now = Date.now()
  return (
    moment.tz(now, regionTz).utcOffset() === moment.tz(now, localTz).utcOffset()
  )
}

function zoneAbbr(tz, moment) {
  if (!tz) return ''
  return moment.tz(tz).format('z')
}

function isLocalDisplay(options) {
  return (
    options.allow === true &&
    options.mode === 'local' &&
    options.regionTz &&
    options.localTz
  )
}

function displayCalendarToday(options) {
  const { moment } = options
  if (isLocalDisplay(options))
    return moment.tz(options.localTz).format('YYYY-MM-DD')
  return moment().format('YYYY-MM-DD')
}

function expandDatesToDisplayDays(dates, options) {
  if (!Array.isArray(dates)) return []
  if (!isLocalDisplay(options)) return dates.slice()
  const { moment, regionTz, localTz } = options
  const days = new Set()
  for (const value of dates) {
    const key = moment(value).format('YYYY-MM-DD')
    if (!moment(key, 'YYYY-MM-DD', true).isValid()) continue
    const start = moment
      .tz(key + ' 00:00:00', 'YYYY-MM-DD HH:mm:ss', regionTz)
      .tz(localTz)
    const end = moment
      .tz(key + ' 23:59:59', 'YYYY-MM-DD HH:mm:ss', regionTz)
      .tz(localTz)
    const cursor = start.clone().startOf('day')
    const last = end.clone().startOf('day')
    while (cursor.isSameOrBefore(last, 'day')) {
      days.add(cursor.format('YYYY-MM-DD'))
      cursor.add(1, 'day')
    }
  }
  return Array.from(days).sort()
}

function displayWeekStartKey(weekStartsOnSunday, options) {
  const kind = weekStartsOnSunday ? 'week' : 'isoWeek'
  const { moment } = options
  const studio = moment()
    .clone()
    .startOf(kind)
  if (!isLocalDisplay(options)) return studio.format('YYYY-MM-DD')
  const local = moment
    .tz(options.localTz)
    .clone()
    .startOf(kind)
  return (studio.isAfter(local) ? local : studio).format('YYYY-MM-DD')
}

// Same rule as customer-app's DateCarousel: when today is past the first day
// of the week, add one more week so the full calendar_view is still ahead.
function calendarWeekCount(totalWeeks, weekStartsOnSunday, options) {
  const kind = weekStartsOnSunday ? 'week' : 'isoWeek'
  const today = options.moment()
  const studioWeekStart = today.clone().startOf(kind)
  return today.isAfter(studioWeekStart, 'day') ? totalWeeks + 1 : totalWeeks
}

module.exports = {
  STORAGE_KEY,
  resolveDisplayMode,
  isLocalMode,
  toDisplayMoment,
  offsetsMatch,
  zoneAbbr,
  displayCalendarToday,
  expandDatesToDisplayDays,
  displayWeekStartKey,
  calendarWeekCount
}
